// Staff's rules per country (Admin → Countries; packages/db schema/countries.ts): which networks
// Earn shows there and in what order, and whether GaintPlay is open there at all. Pure functions:
// the api Worker reads the rules (lib/country-rules.ts) and applies them with these.
import type { CountryAccess } from '@gaintplay/db';

export interface CountryRuleView {
  access: CountryAccess;
  networkOrder: readonly string[] | null;
  hiddenNetworks: readonly string[] | null;
}

/** "in", " IN " → "IN"; anything that isn't two letters → null (Cloudflare's XX and T1 included). */
export function countryCode(v: string | null | undefined): string | null {
  const c = (v ?? '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(c) && c !== 'XX' ? c : null;
}

/** Is this network kept from members in this country? */
export function hiddenIn(rule: CountryRuleView | null | undefined, providerId: string): boolean {
  return !!rule?.hiddenNetworks?.includes(providerId);
}

/**
 * The country's list: its hidden networks out, then the ones staff ordered for it, first to last,
 * and after them the rest as they came (`items` arrive in the usual order).
 */
export function arrangeForCountry<T extends { id: string }>(
  items: readonly T[],
  rule: CountryRuleView | null | undefined,
): T[] {
  const shown = items.filter((x) => !hiddenIn(rule, x.id));
  const order = rule?.networkOrder ?? [];
  if (!order.length) return shown;
  const rank = new Map(order.map((id, i) => [id, i]));
  return shown
    .map((x, i) => ({ x, i }))
    .sort((a, b) => {
      const ra = rank.get(a.x.id);
      const rb = rank.get(b.x.id);
      if (ra !== undefined && rb !== undefined) return ra - rb;
      if (ra !== undefined) return -1;
      if (rb !== undefined) return 1;
      return a.i - b.i;
    })
    .map(({ x }) => x);
}

/**
 * The strictest access of the places a request concerns (where it comes from, and the member's
 * own country): blocked wins over no_signups wins over open.
 */
export function strictestAccess(
  rules: ReadonlyMap<string, Pick<CountryRuleView, 'access'>>,
  countries: readonly (string | null | undefined)[],
): CountryAccess {
  let out: CountryAccess = 'open';
  for (const c of countries) {
    const a = c ? rules.get(c)?.access : undefined;
    if (a === 'blocked') return 'blocked';
    if (a === 'no_signups') out = 'no_signups';
  }
  return out;
}

/** A rule that changes nothing (everything open, usual order, nothing hidden) isn't kept. */
export function isDefaultRule(r: CountryRuleView & { note?: string | null }): boolean {
  return (
    r.access === 'open' &&
    !r.networkOrder?.length &&
    !r.hiddenNetworks?.length &&
    !(r.note ?? '').trim()
  );
}
